// stereo-core: set windows (circular, curved trend/plunge, freehand) with
// wrap-around across the primitive, and per-set statistics.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, DEG, axialAngle, unit } from "./vec.js";
import { lineToVec, vecToLine, wrap360, poleToPlane } from "./orient.js";
import { Projection, Method, Hemisphere, XY } from "./projection.js";
import { fisherStats, FisherStats } from "./stats.js";

// ---------- extended projection (no hemisphere folding) ----------
// Points of the "other" hemisphere land outside the primitive (r > 1). Used so a
// window drawn across the rim naturally captures poles on the opposite side.
const sgn = (h: Hemisphere) => (h === "lower" ? -1 : 1);
// [PRJ-04]
export function projectExt(v0: Vec3, p: Projection): XY {
  const v = unit(v0), s = sgn(p.hemisphere);
  const theta = Math.acos(Math.max(-1, Math.min(1, s * v[2]))); // 0 = centre, π/2 = rim
  const h = Math.hypot(v[0], v[1]);
  if (h < 1e-12) return { x: 0, y: 0 };
  const r = p.method === "equal-area" ? Math.SQRT2 * Math.sin(theta / 2) : Math.tan(Math.min(theta, 2.8) / 2);
  return { x: (r * v[0]) / h, y: (r * v[1]) / h };
}
export function unprojectExt(pt: XY, p: Projection): Vec3 {
  const r = Math.hypot(pt.x, pt.y), s = sgn(p.hemisphere);
  if (r < 1e-12) return [0, 0, s];
  const theta = p.method === "equal-area" ? 2 * Math.asin(Math.min(1, r / Math.SQRT2)) : 2 * Math.atan(r);
  const st = Math.sin(theta);
  return [(st * pt.x) / r, (st * pt.y) / r, s * Math.cos(theta)];
}

// ---------- windows ----------
export interface CircularWindow { kind: "circle"; axis: Vec3; radius: number }            // degrees
/** Trend range starts at `trendStart`, runs clockwise `trendWidth` degrees.
 *  Plunge range may go below 0 to wrap across the primitive. */
export interface CurvedWindow { kind: "curved"; trendStart: number; trendWidth: number; plungeMin: number; plungeMax: number }
/** Freehand polygon, stored as sphere vectors plus the projection it was drawn in. */
export interface FreehandWindow { kind: "freehand"; vertices: Vec3[]; method: Method; hemisphere: Hemisphere }
export type SetWindow = CircularWindow | CurvedWindow | FreehandWindow;

function inPolygon(pt: XY, poly: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.y > pt.y) !== (b.y > pt.y) && pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
const inTrend = (t: number, w: CurvedWindow) => wrap360(t - w.trendStart) <= w.trendWidth + 1e-9;

// [SET-01]
/** Is the axis `pole` inside the window? Both senses of the axis are tested. */
export function windowContains(w: SetWindow, pole: Vec3): boolean {
  switch (w.kind) {
    case "circle": return axialAngle(pole, w.axis) <= w.radius + 1e-9;
    case "curved": {
      const { trend, plunge } = vecToLine(pole);
      const inBox = (t: number, p: number) => inTrend(t, w) && p >= w.plungeMin - 1e-9 && p <= w.plungeMax + 1e-9;
      return inBox(trend, plunge) || inBox(wrap360(trend + 180), -plunge);
    }
    case "freehand": {
      const P: Projection = { method: w.method, hemisphere: w.hemisphere };
      const poly = w.vertices.map((v) => projectExt(v, P));
      const u = unit(pole);
      return inPolygon(projectExt(u, P), poly) || inPolygon(projectExt([-u[0], -u[1], -u[2]], P), poly);
    }
  }
}

/** Outline of a window as polylines in the plotting projection, including its
 *  wrapped image on the opposite side. Caller clips to the primitive. */
export function windowOutline(w: SetWindow, p: Projection, steps = 90): XY[][] {
  let ring: Vec3[] = [];
  if (w.kind === "circle") {
    const a = unit(w.axis);
    const ref: Vec3 = Math.abs(a[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const e1 = unit([a[1] * ref[2] - a[2] * ref[1], a[2] * ref[0] - a[0] * ref[2], a[0] * ref[1] - a[1] * ref[0]]);
    const e2: Vec3 = [a[1] * e1[2] - a[2] * e1[1], a[2] * e1[0] - a[0] * e1[2], a[0] * e1[1] - a[1] * e1[0]];
    const cb = Math.cos(w.radius * DEG), sb = Math.sin(w.radius * DEG);
    for (let i = 0; i <= steps * 2; i++) {
      const t = (Math.PI * i) / steps;
      ring.push([a[0] * cb + sb * (e1[0] * Math.cos(t) + e2[0] * Math.sin(t)),
        a[1] * cb + sb * (e1[1] * Math.cos(t) + e2[1] * Math.sin(t)),
        a[2] * cb + sb * (e1[2] * Math.cos(t) + e2[2] * Math.sin(t))]);
    }
  } else if (w.kind === "curved") {
    const n = Math.max(8, Math.ceil(w.trendWidth / 2)), m = Math.max(4, Math.ceil((w.plungeMax - w.plungeMin) / 2));
    for (let i = 0; i <= n; i++) ring.push(lineToVec(w.trendStart + (w.trendWidth * i) / n, w.plungeMin));
    for (let i = 0; i <= m; i++) ring.push(lineToVec(w.trendStart + w.trendWidth, w.plungeMin + ((w.plungeMax - w.plungeMin) * i) / m));
    for (let i = n; i >= 0; i--) ring.push(lineToVec(w.trendStart + (w.trendWidth * i) / n, w.plungeMax));
    for (let i = m; i >= 0; i--) ring.push(lineToVec(w.trendStart, w.plungeMin + ((w.plungeMax - w.plungeMin) * i) / m));
  } else {
    // densify edges in the projection the polygon was drawn in
    const P0: Projection = { method: w.method, hemisphere: w.hemisphere };
    const pts = w.vertices.map((v) => projectExt(v, P0));
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      for (let k = 0; k < 6; k++) ring.push(unprojectExt({ x: a.x + ((b.x - a.x) * k) / 6, y: a.y + ((b.y - a.y) * k) / 6 }, P0));
    }
    ring.push(ring[0]);
  }
  // lower-hemisphere lines (plunge up = z>0 in lower mode) are expressed with lineToVec's
  // convention z = -sin(plunge); negative plunge -> z > 0 -> outside the primitive. Good.
  const main = ring.map((v) => projectExt(v, p));
  const twin = ring.map((v) => projectExt([-v[0], -v[1], -v[2]], p));
  return [main, twin];
}

// ---------- sets ----------
export interface JointSetDef { id: string; name: string; window: SetWindow; friction?: number | null }
export interface SetSummary {
  id: string; name: string; members: number[]; stats: FisherStats | null;
  meanPlane: { dip: number; dipDir: number } | null;
}

// [SET-03]
/** Reference direction of a window: circle axis, centre of a curved window,
 *  or the mean of a freehand polygon's vertices. */
export function windowCenter(w: SetWindow): Vec3 {
  if (w.kind === "circle") return unit(w.axis);
  if (w.kind === "curved") return lineToVec(w.trendStart + w.trendWidth / 2, (w.plungeMin + w.plungeMax) / 2);
  const ref = w.vertices[0]; let s: Vec3 = [0, 0, 0];
  for (const v0 of w.vertices) { const v = unit(v0), sg = v[0] * ref[0] + v[1] * ref[1] + v[2] * ref[2] < 0 ? -1 : 1; s = [s[0] + sg * v[0], s[1] + sg * v[1], s[2] + sg * v[2]]; }
  return unit(s);
}

// [SET-02]
/** Assign each pole to a set (0 = none, k = sets[k-1]). A pole inside several
 *  windows goes to the window whose centre is nearest (ties: the earlier set),
 *  the same rule DSE uses for its cones. */
export function assignSets(poles: Vec3[], sets: JointSetDef[]): Int32Array {
  const out = new Int32Array(poles.length);
  const centres = sets.map((s) => windowCenter(s.window));
  poles.forEach((v, i) => {
    let best = Infinity;
    for (let k = 0; k < sets.length; k++) {
      if (!windowContains(sets[k].window, v)) continue;
      const d = axialAngle(v, centres[k]);
      if (d < best) { best = d; out[i] = k + 1; }
    }
  });
  return out;
}

// [STA-08]
export function summariseSets(poles: Vec3[], sets: JointSetDef[], assignment: Int32Array,
  weights?: number[], level = 0.95): SetSummary[] {
  return sets.map((s, k) => {
    const members: number[] = [];
    assignment.forEach((a, i) => { if (a === k + 1) members.push(i); });
    if (!members.length) return { id: s.id, name: s.name, members, stats: null, meanPlane: null };
    const vs = members.map((i) => poles[i]);
    let w = weights ? members.map((i) => weights[i]) : undefined;
    if (w) { // normalise so Σw = n (Diederichs 1990, eq. 3.7): K keeps its meaning under weighting
      const sum = w.reduce((a, b) => a + b, 0); w = w.map((x) => (x * members.length) / sum);
    }
    const st = fisherStats(vs, { weights: w, level });
    return { id: s.id, name: s.name, members, stats: st, meanPlane: poleToPlane(st.mean) };
  });
}

// ---------- editing windows on the sphere ----------
/** Rotation matrix taking unit vector a onto unit vector b (Rodrigues). */
export function rotationBetween(a0: Vec3, b0: Vec3): number[][] {
  const a = unit(a0), b = unit(b0);
  const k: Vec3 = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const s = Math.hypot(...k), c = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  if (s < 1e-12) return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  const [x, y, z] = [k[0] / s, k[1] / s, k[2] / s], C = 1 - c;
  return [[c + x * x * C, x * y * C - z * s, x * z * C + y * s],
    [y * x * C + z * s, c + y * y * C, y * z * C - x * s],
    [z * x * C - y * s, z * y * C + x * s, c + z * z * C]];
}
const apply = (m: number[][], v: Vec3): Vec3 => [
  m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
  m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
  m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2]];

// [SET-04]
/** Move a window so that the direction `from` goes to `to` (a drag on the net). */
export function moveWindow(w: SetWindow, from: Vec3, to: Vec3): SetWindow {
  if (w.kind === "circle") return { ...w, axis: apply(rotationBetween(from, to), w.axis) };
  if (w.kind === "freehand") { const m = rotationBetween(from, to); return { ...w, vertices: w.vertices.map((v) => apply(m, v)) }; }
  // curved windows live in trend/plunge space: shift both ranges
  const a = vecToLineSigned(from), b = vecToLineSigned(to);
  const dT = ((b.trend - a.trend + 540) % 360) - 180, dP = b.plunge - a.plunge;
  const span = w.plungeMax - w.plungeMin;
  let lo = w.plungeMin + dP; lo = Math.max(-30, Math.min(90 - span, lo));
  return { ...w, trendStart: wrap360(w.trendStart + dT), plungeMin: lo, plungeMax: lo + span };
}
/** Trend/plunge without folding: upward vectors get negative plunge. */
export function vecToLineSigned(v0: Vec3) {
  const v = unit(v0);
  return { trend: wrap360(Math.atan2(v[0], v[1]) / DEG), plunge: Math.asin(Math.max(-1, Math.min(1, -v[2]))) / DEG };
}
